/**
 * Vendor Insights Utility Functions
 * Port of vendorInsights.ts from v1.1-admin-uplift branch
 * 
 * Provides:
 * - Payment calculations (paid, remaining, contract totals)
 * - Risk assessment (level, label, dot signals)
 * - Date utilities (daysUntil, formatShortDate)
 * - Contract insights extraction (OCR pattern matching)
 * - Vendor categorization and grouping
 */

// ─── Vendor Category Groups ────────────────────────────────────────────────────
export const VENDOR_CATEGORY_GROUPS = [
  {
    label: 'Ceremony & Reception',
    group: 'Venue',
    options: ['Venue', 'Catering', 'Bar Service', 'Rentals'],
  },
  {
    label: 'Creative & Experience',
    group: 'Production',
    options: ['Photography', 'Videography', 'Flowers', 'Stationery', 'Decor'],
  },
  {
    label: 'Entertainment',
    group: 'Entertainment',
    options: ['DJ', 'Band', 'Entertainment', 'MC'],
  },
  {
    label: 'Logistics & Support',
    group: 'Production',
    options: ['Transportation', 'Planner', 'Coordinator', 'Beauty', 'Hair/Makeup', 'Cake', 'Security', 'Other'],
  },
];

export const VENDOR_CATEGORIES = VENDOR_CATEGORY_GROUPS.flatMap((group) => group.options);

export const EXPERIENCE_GROUP_ORDER = ['Culinary', 'Production', 'Entertainment', 'Venue'];

// ─── Status Configuration ──────────────────────────────────────────────────────
export const STATUS_CONFIG = {
  confirmed: { bg: '#F0FAF5', border: '#A3D4B8', text: '#2F7A57', label: 'Booked' },
  Hired: { bg: '#F0FAF5', border: '#A3D4B8', text: '#2F7A57', label: 'Hired' },
  pending: { bg: '#FFFBEF', border: '#D4B96A', text: '#9B7420', label: 'Pending' },
  Considering: { bg: '#FFFBEF', border: '#D4B96A', text: '#9B7420', label: 'Considering' },
  Shortlisted: { bg: '#F0F2FF', border: 'rgba(91,110,174,0.35)', text: '#5B6EAE', label: 'Shortlisted' },
  cancelled: { bg: '#FFF7F7', border: '#E8B4B8', text: '#B24E4E', label: 'Cancelled' },
  Canceled: { bg: '#FFF7F7', border: '#E8B4B8', text: '#B24E4E', label: 'Canceled' },
  negotiating: { bg: '#F0F2FF', border: 'rgba(91,110,174,0.35)', text: '#5B6EAE', label: 'Negotiating' },
  InProgress: { bg: '#FFFBEF', border: '#D4B96A', text: '#f59e0b', label: 'In Progress' },
  Completed: { bg: '#F0FAF5', border: '#A3D4B8', text: '#10b981', label: 'Completed' },
};

export const PAYMENT_METHOD_LABELS = {
  cash: 'Cash',
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  check: 'Check',
  bank_transfer: 'Bank Transfer / Zelle',
  venmo: 'Venmo',
  paypal: 'PayPal',
  other: 'Other',
};

// ─── Colors ────────────────────────────────────────────────────────────────────
export const COLORS = {
  gold: '#C6A75A',
  goldDark: '#A8893C',
  success: '#22c55e',
  error: '#dc2626',
  warning: '#f59e0b',
  grey: '#9ca3af',
  greyDark: '#6b7280',
  white: '#FFFFFF',
  cream: '#FAF6EE',
  textPrimary: '#2A2010',
  textSecondary: '#7A6D52',
};

// ─── Utility Functions ─────────────────────────────────────────────────────────

function asNumber(value) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function daysUntil(value) {
  const date = parseDate(value);
  if (!date) return null;
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function formatShortDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatFullDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatMoney(val) {
  if (val == null || val === '') return '—';
  const num = asNumber(val);
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

export function formatPhone(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return phone;
}

export function formatPhoneInput(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// ─── Vendor Payment Calculations ───────────────────────────────────────────────

/**
 * Get total amount paid for a vendor
 * Prefers payments array (source of truth), falls back to paid_to_date
 */
export function getVendorPaid(vendor) {
  // If payment ledger entries exist, derive total from them
  const entries = vendor.payments;
  if (entries && entries.length > 0) {
    return entries.reduce((sum, e) => sum + asNumber(e.amount), 0);
  }
  // Fall back to manually-typed amount
  return asNumber(
    vendor.paid_to_date ??
    vendor.amount_paid ??
    vendor.paid ??
    0
  );
}

/**
 * Get contract total for a vendor
 */
export function getVendorContract(vendor) {
  return asNumber(
    vendor.contract_total ??
    vendor.amount ??
    vendor.contract_amount ??
    0
  );
}

/**
 * Get remaining balance for a vendor
 */
export function getVendorRemaining(vendor) {
  const contract = getVendorContract(vendor);
  const paid = getVendorPaid(vendor);
  return Math.max(contract - paid, 0);
}

/**
 * Get the last payment entry for a vendor
 */
export function getLastPayment(vendor) {
  const entries = vendor.payments;
  if (!entries || entries.length === 0) return null;
  return [...entries].sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0];
}

/**
 * Get payment count for a vendor
 */
export function getPaymentCount(vendor) {
  return vendor.payments?.length ?? 0;
}

// ─── Vendor Risk Assessment ────────────────────────────────────────────────────

/**
 * Check if vendor is missing a contract.
 *
 * The vendor record has no `contract_signed` / `contract_url` / `no_contract`
 * field — the real signal is an entry in the `attachments` array whose
 * `type` is "contract" (see backend vendor attachment schema). A vendor is
 * "missing a contract" only when none of its attachments is a contract.
 * A legacy `contract_url` string, if present on older records, also counts
 * as having a contract on file.
 */
export function isMissingContract(vendor) {
  if (!vendor) return false;
  const attachments = Array.isArray(vendor.attachments) ? vendor.attachments : [];
  const hasContractAttachment = attachments.some((a) => a?.type === 'contract');
  const hasLegacyContractUrl = Boolean(vendor.contract_url);
  return !hasContractAttachment && !hasLegacyContractUrl;
}

/**
 * Get risk level for a vendor: 'none' | 'warning' | 'high'
 */
export function getRiskLevel(vendor) {
  const dueIn = daysUntil(vendor.due_date ?? vendor.balance_due_date ?? vendor.next_due_at);
  const remaining = getVendorRemaining(vendor);

  if (isMissingContract(vendor) && getVendorContract(vendor) > 0) return 'high';
  if (remaining > 0 && dueIn !== null && dueIn < 0) return 'high';
  if (remaining > 0 && dueIn !== null && dueIn <= 7) return 'warning';

  return 'none';
}

/**
 * Get human-readable risk label
 */
export function getRiskLabel(vendor) {
  const dueIn = daysUntil(vendor.due_date ?? vendor.balance_due_date ?? vendor.next_due_at);
  const remaining = getVendorRemaining(vendor);

  if (isMissingContract(vendor) && getVendorContract(vendor) > 0) return 'Missing contract';
  if (remaining > 0 && dueIn !== null && dueIn < 0) return 'Overdue payment';
  if (remaining > 0 && dueIn !== null && dueIn <= 7) return `Due in ${dueIn} day${dueIn === 1 ? '' : 's'}`;
  if (remaining === 0 && getVendorContract(vendor) > 0) return 'Fully paid';

  return 'On track';
}

/**
 * Get primary vendor status text
 */
export function getPrimaryVendorStatus(vendor) {
  const dueIn = daysUntil(vendor.due_date ?? vendor.balance_due_date ?? vendor.next_due_at);
  const remaining = getVendorRemaining(vendor);
  const contract = getVendorContract(vendor);

  if (remaining === 0 && contract > 0) return 'Fully paid';
  if (dueIn !== null && dueIn < 0) return 'Overdue';
  if (dueIn !== null && dueIn <= 7) return 'Due soon';
  if (dueIn !== null && dueIn <= 30) return 'Upcoming';
  
  return vendor.vendor_status || vendor.status || 'Pending';
}

/**
 * Get dot signal for status indicator: { color, blink }
 */
export function getDotSignal(vendor) {
  const dueIn = daysUntil(vendor.due_date ?? vendor.balance_due_date ?? vendor.next_due_at);
  const remaining = getVendorRemaining(vendor);
  const contract = getVendorContract(vendor);

  // Fully paid → green solid
  if (remaining === 0 && contract > 0) {
    return { color: COLORS.success, blink: false };
  }
  // No due date → neutral grey
  if (dueIn === null) {
    return { color: COLORS.grey, blink: false };
  }
  // Overdue → red blink
  if (dueIn < 0) {
    return { color: COLORS.error, blink: true };
  }
  // ≤ 30 days → red solid
  if (dueIn <= 30) {
    return { color: COLORS.error, blink: false };
  }
  // 31–60 days → amber solid
  if (dueIn <= 60) {
    return { color: COLORS.warning, blink: false };
  }
  // > 60 days → green solid
  return { color: COLORS.success, blink: false };
}

/**
 * Get vendor status tone (background/border colors)
 */
export function getVendorStatusTone(statusColor) {
  if (statusColor === COLORS.success) {
    return { bg: '#F0FAF5', border: 'rgba(47,122,87,0.22)' };
  }
  if (statusColor === COLORS.error) {
    return { bg: '#FFF4F4', border: 'rgba(184,32,32,0.20)' };
  }
  if (statusColor === COLORS.gold || statusColor === COLORS.warning) {
    return { bg: '#FFF8EA', border: 'rgba(198,167,90,0.28)' };
  }
  return { bg: '#F8F1E3', border: 'rgba(198,167,90,0.18)' };
}

// ─── Vendor Categorization ─────────────────────────────────────────────────────

/**
 * Get experience group for a vendor category
 */
export function getVendorExperienceGroup(category) {
  const normalized = (category || '').toLowerCase();
  if (normalized.includes('venue')) return 'Venue';
  if (normalized.includes('catering') || normalized.includes('bar') || normalized.includes('cake')) return 'Culinary';
  if (normalized.includes('dj') || normalized.includes('band') || normalized.includes('entertainment') || normalized.includes('mc')) return 'Entertainment';
  return 'Production';
}

/**
 * Get search text for a vendor (for filtering)
 */
export function getVendorSearchText(vendor) {
  return [
    vendor.company_name,
    vendor.name,
    vendor.role_category,
    vendor.category,
    vendor.contact_name,
    vendor.contact,
    vendor.email,
    vendor.phone,
    vendor.notes,
    vendor.address,
    vendor.website,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * Sort vendors by various keys
 */
export function sortVendors(items, sortKey) {
  const sorted = [...items];
  sorted.sort((a, b) => {
    if (sortKey === 'name_asc') {
      return (a.company_name || a.name || '').localeCompare(b.company_name || b.name || '');
    }
    if (sortKey === 'contract_desc') {
      return getVendorContract(b) - getVendorContract(a);
    }
    if (sortKey === 'balance_desc') {
      return getVendorRemaining(b) - getVendorRemaining(a);
    }
    // Default: due_date
    const dueA = daysUntil(a.due_date ?? a.balance_due_date ?? a.next_due_at);
    const dueB = daysUntil(b.due_date ?? b.balance_due_date ?? b.next_due_at);
    if (dueA === null && dueB === null) return (a.company_name || '').localeCompare(b.company_name || '');
    if (dueA === null) return 1;
    if (dueB === null) return -1;
    if (dueA !== dueB) return dueA - dueB;
    return (a.company_name || '').localeCompare(b.company_name || '');
  });
  return sorted;
}

/**
 * Group vendors by experience category
 */
export function groupVendorsByExperience(vendors) {
  const groups = {};
  EXPERIENCE_GROUP_ORDER.forEach(group => {
    groups[group] = [];
  });

  vendors.forEach(vendor => {
    const group = getVendorExperienceGroup(vendor.role_category || vendor.category);
    if (!groups[group]) groups[group] = [];
    groups[group].push(vendor);
  });

  return EXPERIENCE_GROUP_ORDER
    .filter(group => groups[group].length > 0)
    .map(group => ({
      group,
      items: groups[group],
    }));
}

// ─── Filter Functions ──────────────────────────────────────────────────────────

/**
 * Get vendors with missing contracts
 */
export function getMissingContractVendors(vendors) {
  return vendors.filter(v => isMissingContract(v) && getVendorContract(v) > 0);
}

/**
 * Get vendors with upcoming payments (within N days)
 */
export function getUpcomingPaymentVendors(vendors, withinDays = 30) {
  return vendors
    .filter(v => {
      const remaining = getVendorRemaining(v);
      if (remaining <= 0) return false;
      const dueIn = daysUntil(v.due_date ?? v.balance_due_date ?? v.next_due_at);
      return dueIn !== null && dueIn <= withinDays && dueIn >= 0;
    })
    .sort((a, b) => {
      const dueA = daysUntil(a.due_date ?? a.balance_due_date ?? a.next_due_at) ?? 999;
      const dueB = daysUntil(b.due_date ?? b.balance_due_date ?? b.next_due_at) ?? 999;
      return dueA - dueB;
    });
}

/**
 * Get most urgent vendor for alert banner
 */
export function getUrgentVendor(vendors) {
  const urgent = vendors
    .filter(v => {
      const remaining = getVendorRemaining(v);
      if (remaining <= 0) return false;
      const dueIn = daysUntil(v.due_date ?? v.balance_due_date ?? v.next_due_at);
      return dueIn !== null && dueIn <= 2;
    })
    .sort((a, b) => {
      const dueA = daysUntil(a.due_date ?? a.balance_due_date ?? a.next_due_at) ?? 999;
      const dueB = daysUntil(b.due_date ?? b.balance_due_date ?? b.next_due_at) ?? 999;
      return dueA - dueB;
    });
  return urgent[0] || null;
}

/**
 * Filter vendors by ledger filter key
 */
export function getLedgerFilterMatch(vendor, filter) {
  switch (filter) {
    case 'missing_contracts':
      return isMissingContract(vendor);
    case 'deposits_needed':
      return (vendor.deposit_amount ?? 0) > 0 && !vendor.deposit_paid;
    case 'due_this_week':
      return getRiskLevel(vendor) === 'warning';
    case 'overdue':
      return getRiskLevel(vendor) === 'high' && !isMissingContract(vendor);
    default:
      return true;
  }
}

// ─── OCR / Contract Insights ───────────────────────────────────────────────────

/**
 * Extract contract insights from text (client-side OCR pattern matching)
 */
export function extractContractInsights(text) {
  const result = {
    totalAmount: null,
    dueDates: [],
  };

  if (!text) return result;

  // Find dollar amounts
  const amountRegex = /\$[\d,]+(?:\.\d{2})?/g;
  const amounts = text.match(amountRegex) || [];
  const parsedAmounts = amounts
    .map(a => parseFloat(a.replace(/[$,]/g, '')))
    .filter(a => !isNaN(a) && a > 0)
    .sort((a, b) => b - a);
  
  if (parsedAmounts.length > 0) {
    result.totalAmount = parsedAmounts[0]; // Largest amount is likely the total
  }

  // Find dates
  const dateRegex = /\b(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+\d{1,2},?\s+\d{4})\b/gi;
  const dates = text.match(dateRegex) || [];
  
  dates.forEach(dateStr => {
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      result.dueDates.push(parsed.toISOString().slice(0, 10));
    }
  });

  return result;
}

// ─── Timeline Suggestions ──────────────────────────────────────────────────────

/**
 * Suggest payment timeline based on vendor and event date
 */
export function suggestVendorTimeline(vendor, eventDate) {
  const result = {
    depositDueAt: null,
    finalPaymentDue: null,
    nextPaymentDueAt: null,
    suggestedDepositAmount: 0,
  };

  if (!eventDate) return result;

  const event = new Date(eventDate);
  if (isNaN(event.getTime())) return result;

  const contract = getVendorContract(vendor);
  
  // Suggest 50% deposit due 90 days before event
  const depositDate = new Date(event);
  depositDate.setDate(depositDate.getDate() - 90);
  result.depositDueAt = depositDate.toISOString().slice(0, 10);
  result.suggestedDepositAmount = Math.round(contract * 0.5);

  // Final payment due 14 days before event
  const finalDate = new Date(event);
  finalDate.setDate(finalDate.getDate() - 14);
  result.finalPaymentDue = finalDate.toISOString().slice(0, 10);

  return result;
}

/**
 * Get schedule mismatches between vendor dates and event date
 */
export function getScheduleMismatch(vendor, eventDate) {
  const mismatches = [];
  
  if (!eventDate) return mismatches;
  
  const event = new Date(eventDate);
  if (isNaN(event.getTime())) return mismatches;

  // Check service date
  if (vendor.service_date) {
    const serviceDate = new Date(vendor.service_date);
    if (!isNaN(serviceDate.getTime())) {
      const diffDays = Math.abs(Math.round((serviceDate - event) / (1000 * 60 * 60 * 24)));
      if (diffDays > 1) {
        mismatches.push({
          field: 'date',
          detail: `Service date (${formatShortDate(vendor.service_date)}) doesn't match event date (${formatShortDate(eventDate)})`,
        });
      }
    }
  }

  // Check if payment due after event
  const dueDate = vendor.due_date ?? vendor.balance_due_date;
  if (dueDate) {
    const due = new Date(dueDate);
    if (!isNaN(due.getTime()) && due > event) {
      mismatches.push({
        field: 'payment',
        detail: `Payment due (${formatShortDate(dueDate)}) is after the event`,
      });
    }
  }

  return mismatches;
}

// ─── Upgrade Options (for sandbox mode) ────────────────────────────────────────

export function buildUpgradeOptions(group) {
  const options = {
    Culinary: [
      { id: 'late-night', label: 'Late-Night Bites', amount: 650 },
      { id: 'signature-bar', label: 'Signature Bar Upgrade', amount: 1200 },
      { id: 'chef-station', label: 'Chef Action Station', amount: 1800 },
    ],
    Production: [
      { id: 'lighting', label: 'Lighting Upgrade', amount: 900 },
      { id: 'lounge', label: 'Lounge Styling', amount: 1100 },
      { id: 'content', label: 'Content Capture Add-On', amount: 1500 },
    ],
    Entertainment: [
      { id: 'extended-set', label: 'Extended Set', amount: 800 },
      { id: 'mc', label: 'MC Support', amount: 550 },
      { id: 'sound', label: 'Sound Reinforcement', amount: 950 },
    ],
    Venue: [
      { id: 'valet', label: 'Valet Service', amount: 700 },
      { id: 'suite', label: 'Suite Extension', amount: 900 },
      { id: 'security', label: 'Security Upgrade', amount: 600 },
    ],
  };
  return options[group] || [];
}

export default {
  // Categories
  VENDOR_CATEGORY_GROUPS,
  VENDOR_CATEGORIES,
  EXPERIENCE_GROUP_ORDER,
  STATUS_CONFIG,
  PAYMENT_METHOD_LABELS,
  COLORS,
  // Formatting
  formatMoney,
  formatShortDate,
  formatFullDate,
  formatPhone,
  formatPhoneInput,
  parseDate,
  daysUntil,
  // Payment calculations
  getVendorPaid,
  getVendorContract,
  getVendorRemaining,
  getLastPayment,
  getPaymentCount,
  // Risk assessment
  isMissingContract,
  getRiskLevel,
  getRiskLabel,
  getPrimaryVendorStatus,
  getDotSignal,
  getVendorStatusTone,
  // Categorization
  getVendorExperienceGroup,
  getVendorSearchText,
  sortVendors,
  groupVendorsByExperience,
  // Filtering
  getMissingContractVendors,
  getUpcomingPaymentVendors,
  getUrgentVendor,
  getLedgerFilterMatch,
  // OCR
  extractContractInsights,
  // Timeline
  suggestVendorTimeline,
  getScheduleMismatch,
  // Upgrades
  buildUpgradeOptions,
};
